import logger from './logger';
// Chat Translation Utilities
// Used by ChatBox, LobbyChat, SpectatorChat, FriendChat

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple language detection heuristics (basic patterns)
export const detectLanguage = (text) => {
  if (!text) return 'en';
  
  // Check for CJK characters (Chinese, Japanese, Korean)
  if (/[\u4e00-\u9fff]/.test(text)) return 'zh'; // Chinese
  if (/[\u3040-\u309f\u30a0-\u30ff]/.test(text)) return 'ja'; // Japanese
  if (/[\uac00-\ud7af]/.test(text)) return 'ko'; // Korean
  
  // Check for Arabic
  if (/[\u0600-\u06ff]/.test(text)) return 'ar';
  
  // Check for Russian/Cyrillic
  if (/[\u0400-\u04ff]/.test(text)) return 'ru';
  
  // Check for Hindi/Devanagari
  if (/[\u0900-\u097f]/.test(text)) return 'hi';
  
  // Check for common Spanish words
  if (/\b(hola|cómo|qué|está|gracias|por favor|amigo|juego)\b/i.test(text)) return 'es';
  
  // Check for common French words
  if (/\b(bonjour|comment|merci|s'il vous plaît|je suis|oui|non)\b/i.test(text)) return 'fr';
  
  // Check for common German words
  if (/\b(hallo|wie|danke|bitte|ich bin|ja|nein|gut|spiel)\b/i.test(text)) return 'de';
  
  // Check for common Portuguese words
  if (/\b(olá|como|obrigado|por favor|eu sou|jogo)\b/i.test(text)) return 'pt';
  
  // Check for common Italian words
  if (/\b(ciao|come|grazie|per favore|io sono|gioco)\b/i.test(text)) return 'it';
  
  return 'en'; // Default to English
};

// Basic translation dictionary for common chess/game phrases (offline fallback)
export const PHRASE_TRANSLATIONS = {
  en: {
    'hey': 'hey', 'hello': 'hello', 'hi': 'hi', 'gg': 'good game', 'gl': 'good luck', 
    'nice move': 'nice move', 'good game': 'good game', 'thanks': 'thanks', 'good luck': 'good luck',
    'your turn': 'your turn', 'my turn': 'my turn', 'check': 'check', 'checkmate': 'checkmate',
    'nice': 'nice', 'wow': 'wow', 'lol': 'lol', 'haha': 'haha', 'yes': 'yes', 'no': 'no',
    'ok': 'ok', 'okay': 'okay', 'sure': 'sure', 'bye': 'bye', 'goodbye': 'goodbye',
    'sorry': 'sorry', 'oops': 'oops', 'wait': 'wait', 'ready': 'ready', 'go': 'go'
  },
  es: {
    'hey': 'hola', 'hello': 'hola', 'hi': 'hola', 'gg': 'buen juego', 'gl': 'buena suerte',
    'nice move': 'buen movimiento', 'good game': 'buen juego', 'thanks': 'gracias', 'good luck': 'buena suerte',
    'your turn': 'tu turno', 'my turn': 'mi turno', 'check': 'jaque', 'checkmate': 'jaque mate',
    'nice': 'genial', 'wow': 'guau', 'lol': 'jaja', 'haha': 'jaja', 'yes': 'sí', 'no': 'no',
    'ok': 'ok', 'okay': 'vale', 'sure': 'claro', 'bye': 'adiós', 'goodbye': 'adiós',
    'sorry': 'perdón', 'oops': 'ups', 'wait': 'espera', 'ready': 'listo', 'go': 'vamos',
    'hola': 'hello', 'gracias': 'thanks', 'sí': 'yes', 'adiós': 'goodbye'
  },
  fr: {
    'hey': 'salut', 'hello': 'bonjour', 'hi': 'salut', 'gg': 'bien joué', 'gl': 'bonne chance',
    'nice move': 'bon coup', 'good game': 'bien joué', 'thanks': 'merci', 'good luck': 'bonne chance',
    'your turn': 'ton tour', 'my turn': 'mon tour', 'check': 'échec', 'checkmate': 'échec et mat',
    'nice': 'super', 'wow': 'ouah', 'lol': 'mdr', 'haha': 'haha', 'yes': 'oui', 'no': 'non',
    'ok': 'ok', 'okay': "d'accord", 'sure': 'bien sûr', 'bye': 'au revoir', 'goodbye': 'au revoir',
    'sorry': 'désolé', 'oops': 'oups', 'wait': 'attends', 'ready': 'prêt', 'go': 'allez',
    'bonjour': 'hello', 'merci': 'thanks', 'oui': 'yes', 'non': 'no'
  }
  // ... other languages use AI translation
};

// Language display names
export const LANGUAGE_NAMES = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  it: 'Italiano',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  ru: 'Русский',
  ar: 'العربية',
  hi: 'हिन्दी'
};

// Cache for AI translations to avoid repeated API calls
const translationCache = new Map();

// Translate a message from detected language to target language
export const translateMessage = (message, targetLang) => {
  if (!message || !targetLang) return { original: message, translated: null };
  
  const sourceLang = detectLanguage(message);
  
  // If same language, no need to translate
  if (sourceLang === targetLang) return { original: message, translated: null };
  
  const lowerMessage = message.toLowerCase().trim();
  
  // Check if we have a local phrase translation first (faster)
  const sourceDict = PHRASE_TRANSLATIONS[sourceLang];
  const targetDict = PHRASE_TRANSLATIONS[targetLang];
  
  if (sourceDict && targetDict) {
    // Try exact match first
    if (sourceDict[lowerMessage]) {
      const englishKey = sourceLang === 'en' ? lowerMessage : sourceDict[lowerMessage];
      if (targetDict[englishKey]) {
        return { original: message, translated: targetDict[englishKey], sourceLang };
      }
    }
    
    // Try finding the key in reverse (if source message matches a translation value)
    for (const [key, value] of Object.entries(sourceDict)) {
      if (value.toLowerCase() === lowerMessage && targetDict[key]) {
        return { original: message, translated: targetDict[key], sourceLang };
      }
    }
  }
  
  // No local translation available - show language indicator
  // AI translation will be fetched asynchronously via translateMessageAsync
  return { original: message, translated: null, sourceLang, needsAiTranslation: true };
};

// Async AI translation using the backend API
export const translateMessageAsync = async (message, targetLang) => {
  if (!message || !targetLang) return { original: message, translated: null };
  
  const sourceLang = detectLanguage(message);
  
  // If same language, no need to translate
  if (sourceLang === targetLang) return { original: message, translated: null };
  
  // Check cache first
  const cacheKey = `${message}|${sourceLang}|${targetLang}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }
  
  try {
    const response = await fetch(`${API_URL}/api/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: message,
        source_lang: sourceLang,
        target_lang: targetLang
      })
    });
    
    if (!response.ok) {
      throw new Error('Translation API error');
    }
    
    const data = await response.json();
    const result = {
      original: message,
      translated: data.translated !== message ? data.translated : null,
      sourceLang,
      method: data.method
    };
    
    // Cache the result
    translationCache.set(cacheKey, result);
    
    return result;
  } catch (error) {
    logger.error('Translation error:', error);
    return { original: message, translated: null, sourceLang, error: error.message };
  }
};
